import { useLocalSearchParams } from 'expo-router';
import { Bookmark, BookmarkCheck } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Header } from '@/components/header';
import { ResultPostBody } from '@/components/libraryChat/RelatedInquiryDetail';
import { takePendingResults } from '@/components/libraryChat/pendingResultHandoff';
import { buildResultStep } from '@/components/libraryChat/resultStep';
import { useLeadPost } from '@/components/libraryChat/useLeadPost';
import { ROUTES } from '@/constants/routes';
import { useToggleSavedPost } from '@/features/inquiry-library';
import { useAbility } from '@/hooks/useAbility';
import { useBackNavigation } from '@/hooks/useBackNavigation';

/**
 * The compact-layout screen for a Research result on every platform, pushed
 * from `LibraryChatScreen`'s `handleSelectResult`. A push rather than a modal
 * or sheet, so back, swipe and the hardware back button all return to the
 * conversation, which stays mounted underneath. On iOS it shares the
 * conversation's navigation bar, so iOS 26 morphs the glass bar buttons
 * between the two screens.
 *
 * Save is a header item that is always present and disabled while the post
 * loads, never added once it arrives. Stepping loads a new post each time, and
 * a slot whose contents came and went made react-native-screens remount this
 * screen (seen when it was a modal) and lose the reader's place.
 *
 * The title is the bar's own, which on iOS cross-fades with the conversation's
 * on push and pop.
 * See `ROUTES.LIBRARY_CHAT_RESULT` and `pendingResultHandoff.ts` for why the turn's results travel through an
 * in-memory handoff rather than a route param or `AskConversationProvider`: a
 * pushed screen sits outside that provider tree, and a route param reaches
 * Segment as a screen property with no way to know it carries inquiry titles.
 *
 * A sibling of `community/ask/`, not nested inside it, on purpose — that
 * directory's `_layout.tsx` renders its routes through a `Slot`, and per
 * `AskHeaderHost`'s own comment, a `Stack.Screen`/`Stack.Toolbar` declared
 * from inside a `Slot`-rendered route never reaches the real navigator
 * header. Sitting outside it keeps this screen a plain stack screen, so
 * its own header items actually attach.
 *
 * The ability check here only gates the UI shortcut into this screen; the
 * server remains the real enforcement point (see `hooks/useAbility.ts`), so
 * failing it just backs out rather than reproducing the Ask layout's full
 * loading/unavailable states.
 *
 * Backing out uses `useBackNavigation` rather than a bare `router.back()`.
 * This screen is normally reached only by `router.push` from
 * `LibraryChatScreen`, so there is always a screen to return to — but a
 * direct visit (a typed URL, an old bookmark, a shared link) has no
 * navigation history at all, and `router.back()` then silently no-ops
 * (a reader without the ability is the realistic way to hit this).
 * Confirmed live: visiting
 * this route directly on web left the content area blank with no escape
 * but the global nav, instead of landing back on Research.
 *
 * `headerShown: true` is declared statically on this route's registration in
 * `app/(secure)/_layout.tsx`, not here. React Navigation renders a pushed
 * screen off-focus first (to preload it) using only that static
 * registration, before this component's own `<Stack.Screen options={{...}}>`
 * has had a chance to layer anything on top. Toggling `headerShown` from
 * `false` (the layout's `BASE_STACK_SCREEN_OPTIONS` default) to `true` here
 * would make that preload-to-focus transition itself an instance of
 * react-native-screens' "dynamically changing header's visibility in modals"
 * remount — confirmed live: the header showed the route's default title
 * (derived from the file path) rather than `post?.title`, even though
 * this component's render already had the right title, because the remount
 * briefly left the header with only the layout's title-less base options.
 * Keeping `headerShown` static removes that transition; `title` and the rest
 * stay here since they depend on the open post, which the layout can't know.
 */
export default function AskResultScreen() {
  const { bottom } = useSafeAreaInsets();

  const ability = useAbility();

  const { navigateBack } = useBackNavigation(ROUTES.LIBRARY_CHAT);

  const params = useLocalSearchParams<{
    conversationId: string;
    turnId: string;
    resultId: string;
    source: string;
  }>();

  const [handoff] = useState(() => takePendingResults(params.resultId));

  const results = handoff?.results ?? [];

  const [activeResultId, setActiveResultId] = useState(params.resultId);

  const canView = ability.can('read', 'AskConversation');

  useEffect(() => {
    if (!canView) {
      navigateBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navigateBack is rebuilt every render; only canView should re-trigger this
  }, [canView]);

  const activeResult = results.find((entry) => entry.result.resultId === activeResultId)?.result;

  const { post, data, isPending, isError, refetch } = useLeadPost(activeResult);

  const { saved, toggleSaved } = useToggleSavedPost({ item: data, source: 'library_chat' });

  if (!canView) {
    return null;
  }

  const title = post?.title ?? 'Inquiry';

  const step = buildResultStep(results, activeResultId, (target, direction) => {
    handoff?.onStep(target, direction);

    setActiveResultId(target.result.resultId);
  });

  return (
    <>
      <Header
        isBackButton
        backRoute={ROUTES.LIBRARY_CHAT_CONVERSATION(params.conversationId)}
        title={title}
        rightItems={[
          {
            type: 'button',
            key: 'save',
            label: saved ? 'Remove inquiry from saved' : 'Save inquiry',
            icon: {
              sf: saved ? 'bookmark.fill' : 'bookmark',
              lucide: saved ? BookmarkCheck : Bookmark,
            },
            isDisabled: !data,
            onPress: toggleSaved,
          },
        ]}
      />

      <ResultPostBody
        post={post}
        data={data}
        isPending={isPending}
        isError={isError}
        onRetry={refetch}
        showSaveButton={false}
        step={step}
        bottomInset={bottom}
      />
    </>
  );
}
